import type {
  ActiveFilters,
  FilterAction,
  FilterCodec,
  FilterOperation,
} from './types';

function splitCommaSeparatedValues(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function joinCommaSeparatedValues(values: string[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

export function buildRoutePath(
  basePath: string,
  filterKeys: readonly string[],
): string {
  const querySegment = filterKeys.map((key) => `:${key}`).join('&');
  return `${basePath}?${querySegment}`;
}

export function readActiveFiltersFromRouteParams<TFilterKey extends string>(
  routeParams: Record<string, string>,
  filterKeys: readonly TFilterKey[],
  isKnownFilterKey: (key: string) => key is TFilterKey,
): ActiveFilters<TFilterKey> {
  return Object.entries(routeParams).reduce<ActiveFilters<TFilterKey>>(
    (activeFilters, [key, value]) => {
      const isAllowedFilterKey = isKnownFilterKey(key);
      const hasValue = Boolean(value);

      if (!isAllowedFilterKey || !hasValue) {
        return activeFilters;
      }

      return { ...activeFilters, [key]: value };
    },
    {},
  );
}

export function writeRouteParamsFromActiveFilters<TFilterKey extends string>(
  activeFilters: ActiveFilters<TFilterKey>,
  filterKeys: readonly TFilterKey[],
): Record<string, string | undefined> {
  return filterKeys.reduce<Record<string, string | undefined>>(
    (routeParams, key) => {
      const rawValue = activeFilters[key];

      if (!rawValue) {
        return routeParams;
      }

      return { ...routeParams, [key]: rawValue };
    },
    {},
  );
}

export function applyFilterOperation<TFilterKey extends string>(
  activeFilters: ActiveFilters<TFilterKey>,
  operation: FilterOperation<TFilterKey>,
  value?: string,
): ActiveFilters<TFilterKey> {
  const [action, key] = operation.split(':') as [FilterAction, TFilterKey];
  const nextFilters = { ...activeFilters };

  if (action === 'set' && value) {
    nextFilters[key] = value;
  }

  if (action === 'remove') {
    delete nextFilters[key];
  }

  return nextFilters;
}

export function defineRouteFilters<const TFilterKeys extends readonly string[]>(
  filterKeys: TFilterKeys,
) {
  type FilterKey = TFilterKeys[number] & string;

  const isKnownFilterKey = (key: string): key is FilterKey =>
    (filterKeys as readonly string[]).includes(key);

  return {
    filterKeys,
    isKnownFilterKey,
    readFromRoute: (routeParams: Record<string, string> = {}) =>
      readActiveFiltersFromRouteParams(
        routeParams,
        filterKeys,
        isKnownFilterKey,
      ),
    writeToRoute: (activeFilters: ActiveFilters<FilterKey>) =>
      writeRouteParamsFromActiveFilters(activeFilters, filterKeys),
    applyOperation: (
      activeFilters: ActiveFilters<FilterKey>,
      operation: FilterOperation<FilterKey>,
      value?: string,
    ) => applyFilterOperation(activeFilters, operation, value),
  };
}

export function decodeValueFromUrl<TFilterKey extends string>(
  key: TFilterKey,
  rawValue: string,
  codecs: Partial<Record<TFilterKey, FilterCodec>>,
): unknown {
  const codec = codecs[key];
  return codec ? codec.fromUrl(rawValue) : rawValue;
}

export function encodeValueForUrl<TFilterKey extends string>(
  key: TFilterKey,
  value: unknown,
  codecs: Partial<Record<TFilterKey, FilterCodec>>,
): string | undefined {
  const codec = codecs[key];
  return codec ? codec.toUrl(value) : (value as string | undefined);
}

export function createCommaSeparatedCodec(): FilterCodec {
  return {
    fromUrl: splitCommaSeparatedValues,
    toUrl: (value) => joinCommaSeparatedValues(value as string[]),
  };
}

export function createEnumMapCodec<TValue extends string>({
  fromUrl,
  toUrl,
}: {
  fromUrl: Record<string, TValue>;
  toUrl: Record<TValue, string>;
}): FilterCodec {
  return {
    fromUrl: (value) =>
      splitCommaSeparatedValues(value)
        .map((token) => fromUrl[token])
        .filter(Boolean),
    toUrl: (value) =>
      joinCommaSeparatedValues(
        (value as TValue[]).map((item) => toUrl[item]).filter(Boolean),
      ),
  };
}
